import type { WorkerRole } from '../../core/index.js'
import type { DemoFacility, DemoWorker } from './demoData.js'

/**
 * What a large demo adds to the standard dataset so the client is the size
 * of a large operator: enough further facilities to make 316 in all, each
 * with an administrator and a three-worker roster, and enough further
 * operators to make ten. All of it is invented and built from the fixed
 * lists below by position - no randomness - so every large demo is the
 * same, like the reviewed data in demoData.ts.
 *
 * These facilities have a roster with imported scores but no activity: no
 * open shifts, outcomes, rankings or closed months. Reporting months of
 * outcomes for a thousand workers would be tens of thousands of partner
 * calls from the browser, so the activity stays with the standard
 * facilities (see `activeFacilities` in demoPlan.ts).
 */

/** How many facilities a large demo has in all, and how many operators (the contact's login included). */
export const LARGE_FACILITY_TOTAL = 316
export const LARGE_OPERATOR_TOTAL = 10
/** How many of the facilities added for scale get a full roster and history beside the standard four (demo history plan D5: twelve in all). */
export const LARGE_ACTIVE_EXTRA_FACILITIES = 8

/** 39 places and 8 kinds: with no shared factor, stepping through both together gives 312 distinct names before any repeats. */
const PLACES = [
  'Cedar Grove', 'Maple Ridge', 'Willow Creek', 'Oak Hollow', 'Pine Valley', 'Birchwood', 'Elm Park', 'Aspen Hills', 'Magnolia', 'Juniper Point',
  'Sycamore', 'Hawthorne', 'Laurel Springs', 'Chestnut Hill', 'Poplar Bluff', 'Meadowbrook', 'Stonebridge', 'Fairhaven', 'Brookside', 'Clearwater',
  'Summit', 'Prairie Winds', 'Sunrise', 'Heritage', 'Golden Valley', 'Silver Lake', 'Rosewood', 'Foxglove', 'Briarwood', 'Windsor',
  'Ashford', 'Kingsley', 'Bellmont', 'Glenhaven', 'Thornbury', 'Westfield', 'Eastgate', 'Southpointe', 'Millbrook',
] as const
const KINDS = ['Care Center', 'Health & Rehab', 'Nursing Center', 'Senior Living', 'Rehabilitation Center', 'Manor', 'Village', 'Commons'] as const

/** Real cities with a matching ZIP code, area code and zone, so a facility's address and time zone agree; the streets and numbers are invented. */
const LOCATIONS: readonly (readonly [city: string, region: string, postalCode: string, areaCode: string, timezone: string])[] = [
  ['Boston', 'MA', '02108', '617', 'America/New_York'],
  ['Chicago', 'IL', '60601', '312', 'America/Chicago'],
  ['Denver', 'CO', '80202', '303', 'America/Denver'],
  ['Seattle', 'WA', '98101', '206', 'America/Los_Angeles'],
  ['Philadelphia', 'PA', '19103', '215', 'America/New_York'],
  ['Milwaukee', 'WI', '53202', '414', 'America/Chicago'],
  ['Phoenix', 'AZ', '85004', '602', 'America/Phoenix'],
  ['Portland', 'OR', '97204', '503', 'America/Los_Angeles'],
  ['Pittsburgh', 'PA', '15222', '412', 'America/New_York'],
  ['Minneapolis', 'MN', '55401', '612', 'America/Chicago'],
  ['Salt Lake City', 'UT', '84101', '801', 'America/Denver'],
  ['Sacramento', 'CA', '95814', '916', 'America/Los_Angeles'],
  ['Baltimore', 'MD', '21202', '410', 'America/New_York'],
  ['St. Louis', 'MO', '63101', '314', 'America/Chicago'],
  ['Albuquerque', 'NM', '87102', '505', 'America/Denver'],
  ['San Diego', 'CA', '92101', '619', 'America/Los_Angeles'],
  ['Richmond', 'VA', '23219', '804', 'America/New_York'],
  ['Kansas City', 'MO', '64106', '816', 'America/Chicago'],
  ['Charlotte', 'NC', '28202', '704', 'America/New_York'],
  ['Dallas', 'TX', '75201', '214', 'America/Chicago'],
  ['Atlanta', 'GA', '30303', '404', 'America/New_York'],
  ['Houston', 'TX', '77002', '713', 'America/Chicago'],
  ['Tampa', 'FL', '33602', '813', 'America/New_York'],
  ['Omaha', 'NE', '68102', '402', 'America/Chicago'],
  ['Columbus', 'OH', '43215', '614', 'America/New_York'],
  ['Nashville', 'TN', '37203', '615', 'America/Chicago'],
  ['Detroit', 'MI', '48226', '313', 'America/Detroit'],
]
const STREETS = ['Main Street', 'Oak Avenue', 'Park Boulevard', 'Mill Road', 'Church Street', 'Highland Avenue', 'Center Street', 'Lake Drive', 'Washington Avenue', 'Spring Street', 'Orchard Lane'] as const

const FIRST_NAMES = [
  'Avery', 'Jordan', 'Priya', 'Marcus', 'Elena', 'Tobias', 'Naomi', 'Desmond', 'Ingrid', 'Rohan', 'Camille', 'Felix', 'Yara', 'Gideon', 'Maeve',
  'Santiago', 'Lucia', 'Winston', 'Anika', 'Callum', 'Rosalind', 'Emeka', 'Hana', 'Silas', 'Beatriz', 'Otto', 'Leila', 'Cormac', 'Simone',
] as const
const LAST_NAMES = [
  'Hartwell', 'Okonkwo', 'Lindqvist', 'Castellano', 'Brennan', 'Nakamura', 'Fairchild', 'Adeyemi', 'Rosenthal', 'Pemberton', 'Vasquez', 'Holloway', 'Bergstrom',
  'Achterberg', 'Delacroix', 'Kowalczyk', 'Thackeray', 'Mbeki', 'Sorensen', 'Whitcombe', 'Abernathy', 'Fontaine', 'Greaves', 'Iwasaki', 'Lockhart',
  'Montague', 'Novak', 'Oyelaran', 'Prescott', 'Quintero', 'Ravensworth',
] as const

function at<T>(items: readonly T[], index: number): T {
  const item = items[index % items.length]
  if (item === undefined) {
    throw new Error('Nothing to choose from')
  }
  return item
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** An invented person for a position. The lists share no factor, so a pairing only comes round again after 899 positions - and a repeated name is harmless, since identity is always an id. */
export function personAt(index: number): { firstName: string; lastName: string } {
  return { firstName: at(FIRST_NAMES, index), lastName: at(LAST_NAMES, index) }
}

function extraFacility(index: number): DemoFacility {
  const name = `${at(PLACES, index)} ${at(KINDS, index)}`
  const [city, region, postalCode, areaCode, timezone] = at(LOCATIONS, index)
  return {
    facilityId: slugify(name),
    name,
    timezone,
    address: { line1: `${100 + ((index * 37) % 8900)} ${at(STREETS, index)}`, city, region, postalCode, country: 'US' },
    // 555-0100 to 555-0199 is the range set aside for invented numbers.
    phone: `(${areaCode}) 555-01${String(index % 100).padStart(2, '0')}`,
    administrator: personAt(index),
  }
}

/** Each extra facility's roster: a gold, a silver and a bronze, nudged a few points per facility so no two rosters read the same. */
const ROSTER_SEATS: readonly { role: WorkerRole; baseScore: number }[] = [
  { role: 'RN', baseScore: 88 },
  { role: 'LPN', baseScore: 68 },
  { role: 'CNA', baseScore: 49 },
]
const SCORE_NUDGES = [0, 4, -3, 6, -5, 2, -1, 5, -4, 3, -2] as const
/** The worker names start partway through the lists, so a facility's workers never share its administrator's name. */
const WORKER_NAME_OFFSET = 400

function extraWorkers(facility: DemoFacility, facilityIndex: number): DemoWorker[] {
  return ROSTER_SEATS.map(({ role, baseScore }, seat) => {
    const position = facilityIndex * ROSTER_SEATS.length + seat
    const score = baseScore + at(SCORE_NUDGES, facilityIndex + seat * 4)
    const { firstName, lastName } = personAt(WORKER_NAME_OFFSET + position)
    return {
      workerId: `demo-lg-wkr-${String(position + 1).padStart(4, '0')}`,
      facilityId: facility.facilityId,
      name: `${firstName} ${lastName}`,
      role,
      score,
      // The readings a worker with that score would plausibly have. These rosters have no reported outcomes, so the
      // call-out reading is imported like a client's own would be; an active worker's is counted from their outcomes.
      callOuts: score >= 80 ? 1 : score >= 60 ? 3 : 5,
      chartingPct: Math.min(100, Math.round(60 + score * 0.4)),
    }
  })
}

/** The facilities a large demo has beyond the standard ones: as many as bring `standardCount` up to the total. */
export function largeExtraFacilities(standardCount: number): DemoFacility[] {
  return Array.from({ length: Math.max(0, LARGE_FACILITY_TOTAL - standardCount) }, (_, index) => extraFacility(index))
}

/** The rosters of those facilities, in facility order. */
export function largeExtraWorkers(facilities: readonly DemoFacility[]): DemoWorker[] {
  return facilities.flatMap((facility, index) => extraWorkers(facility, index))
}

const OPERATOR_NAME_OFFSET = 200

/** The operators a large demo has beyond the contact's own login and the standard demo's second operator: invented, one per position. */
export function largeExtraOperators(count: number): { firstName: string; lastName: string }[] {
  return Array.from({ length: Math.max(0, count) }, (_, index) => personAt(OPERATOR_NAME_OFFSET + index * 7))
}
